import React, { useState, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import TeachingTooltip from '@/components/TeachingTooltip';

let nextId = 1;
const makeElement = () => ({ id: nextId++, name: 'Element', type: 'transformer', zActual: 0.5, vBase: 13.8 });

const NetworkBuilder = () => {
  const [sBase, setSBase] = useState(100);
  const [elements, setElements] = useState([
    { id: nextId++, name: 'Transformer T1', type: 'transformer', zActual: 2.18, vBase: 13.8 },
    { id: nextId++, name: 'Feeder Line', type: 'line', zActual: 1.05, vBase: 13.8 },
  ]);

  const results = useMemo(() => elements.map(el => {
    const zBase = (el.vBase * el.vBase) / sBase;
    const zpu = zBase > 0 ? el.zActual / zBase : 0;
    return { ...el, zBase, zpu };
  }), [elements, sBase]);

  const totalZpu = useMemo(() => results.reduce((sum, r) => sum + r.zpu, 0), [results]);

  const update = (id, field, value) => {
    setElements(prev => prev.map(el => el.id === id ? { ...el, [field]: value } : el));
  };

  const TYPE_COLORS = { transformer: 'teal', line: 'blue', load: 'amber' };
  const TYPE_LABELS = { transformer: 'Transformer', line: 'Line', load: 'Load' };

  return (
    <div data-testid="network-builder" className="space-y-5">
      {/* System Base */}
      <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-3">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
            System S<sub>base</sub>
          </label>
          <TeachingTooltip
            showIcon
            whatThis="The common MVA base for the entire system. All elements will be converted to this base."
            tryThis="Change from 100 MVA to 10 MVA and watch all Z_pu values scale by 10×."
            whyMatters="Every element in the network must be on the same base before you can add their impedances."
            connectedTo="→ Updates all Z_pu values and the total series impedance below."
          />
        </div>
        <input
          data-testid="network-sbase-input"
          type="number"
          value={sBase}
          min="1"
          max="10000"
          onChange={e => setSBase(Math.max(1, Number(e.target.value)))}
          className="w-24 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2.5 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-teal-500 outline-none"
        />
        <span className="text-sm text-slate-500">MVA</span>
      </div>

      {/* Elements */}
      <div className="space-y-3">
        {elements.map((el) => {
          const res = results.find(r => r.id === el.id);
          return (
            <div
              key={el.id}
              data-testid={`network-element-${el.id}`}
              className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900"
            >
              <div className="flex items-start gap-3 flex-wrap">
                <input
                  type="text"
                  value={el.name}
                  onChange={e => update(el.id, 'name', e.target.value)}
                  className="text-sm font-medium border-b border-slate-200 dark:border-slate-700 bg-transparent text-slate-900 dark:text-white outline-none focus:border-teal-500 py-0.5 min-w-[120px] flex-1"
                />
                <select
                  value={el.type}
                  onChange={e => update(el.id, 'type', e.target.value)}
                  className="text-xs border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:ring-1 focus:ring-teal-500 outline-none"
                >
                  <option value="transformer">Transformer</option>
                  <option value="line">Line</option>
                  <option value="load">Load</option>
                </select>
                <button
                  onClick={() => setElements(prev => prev.filter(e => e.id !== el.id))}
                  className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                  aria-label="Remove element"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 items-end">
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-500 block mb-1">Z_actual (Ω)</label>
                  <input
                    type="number"
                    value={el.zActual}
                    min="0"
                    step="0.001"
                    onChange={e => update(el.id, 'zActual', parseFloat(e.target.value) || 0)}
                    className="w-full text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-teal-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-500 block mb-1">V_base (kV)</label>
                  <input
                    type="number"
                    value={el.vBase}
                    min="0.1"
                    step="0.1"
                    onChange={e => update(el.id, 'vBase', parseFloat(e.target.value) || 0.1)}
                    className="w-full text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-teal-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-500 block mb-1">Z_base (Ω)</label>
                  <div className="text-sm font-mono text-slate-600 dark:text-slate-400 py-1 px-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md">
                    {res?.zBase.toFixed(4)}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-teal-700 dark:text-teal-400 font-semibold block mb-1">Z_pu (pu)</label>
                  <div className="text-sm font-mono font-bold text-teal-700 dark:text-teal-400 py-1 px-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-md">
                    {res?.zpu.toFixed(4)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        data-testid="add-element-btn"
        onClick={() => setElements(prev => [...prev, makeElement()])}
        className="flex items-center gap-2 text-sm text-teal-700 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-200 font-medium transition-colors"
      >
        <Plus className="h-4 w-4" />
        Add element
      </button>

      {/* Series total */}
      {elements.length > 0 && (
        <div
          data-testid="total-zpu"
          className="bg-[#0B172A] dark:bg-teal-900/30 rounded-lg p-4 flex items-center justify-between"
        >
          <div>
            <p className="text-xs text-teal-400 font-semibold uppercase tracking-widest">
              Total Series Z<sub>pu</sub>
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              Sum of all element per-unit impedances (series connection)
            </p>
          </div>
          <p className="text-3xl font-bold text-teal-300 font-mono">{totalZpu.toFixed(4)}</p>
        </div>
      )}
    </div>
  );
};

export default NetworkBuilder;
